import { fetchUtils } from "react-admin";
import { stringify } from "query-string";
import { v4 as uuidv4 } from "uuid";
import { toast } from "react-toastify";
import de from "../i18n/de";
import en from "../i18n/en";
import { getToken } from "../components/LoginPage";
import authProvider from "./authProvider";

const baseURl = window.ENV.intermediary_endpoint + "/api";
const permissionEvent = new CustomEvent("permissionsUpdate", {
  detail: "permissionsUpdate event triggered",
});

const getPickedUser = (usersList, userId) => {
  return Object.values(usersList).find(user => user.id === +userId);
};

const checkLanguage = () => {
  const env = window.ENV.intermediary_endpoint;
  let language;
  switch (env) {
    case "https://api.staging-bycsmessenger.dev/":
      language = navigator.language;
      break;
    case "https://api.messenger.bycs-int.de":
      language = navigator.language;
      break;
    case "https://api.messenger.bycs-vp.de/":
      language = "de";
      break;
    default:
      language = "de";
  }
  return language === "de" ? de : en;
};

const jsonClient = async(url, options = {}) => {
  const token = getToken();
  if (token) {
    options.user = { authenticated: true, token: `Bearer ${token}` };
  }

  try {
    const response = await fetchUtils.fetchJson(url, options);
    return response;
  } catch (err) {
    if (err?.status === 401) {
      console.error("Unauthorized access - perhaps the token has expired.");
      authProvider.logout();
      return Promise.reject(err);
    }
    return Promise.reject(err);
  }
};

const resourceMap = {
  roles: {
    path: "/admin/roles",
    data: "roles",
    total: json => json.data,
  },
  groups: {
    path: "/admin/groups",
    data: "groups",
    total: json => json.data,
  },
  devices: {
    path: "/admin/users/",
    data: "devices",
    map: device => ({
      id: device?.id,
      user_id: device?.user_id,
      device_id: device?.device_id,
      display_name: device?.display_name,
      last_seen_user_agent: device?.last_seen_user_agent,
      last_seen_ts: device?.last_seen_ts,
      last_seen_ip: device?.last_seen_ip,
    }),
    total: json => json.data,
  },
  users: {
    path: "/admin/users",
    map: user => ({
      ...user,
      id: user.id,
      uid: user.uid,
      name: `${user["first_name"]} ${user["last_name"]}`,
      displayname: `${user["first_name"]} ${user["last_name"]}`,
      userName: user.username,
      first_name: user["first_name"],
      last_name: user["last_name"],
      avatar_src: user.avatar,
      role: user?.role_slug,
      admin: !!user["admin_staff"],
      deactivated: !!user["mebis_deactivated"],
      add_guest_to_room: user?.permission?.add_guest_to_room,
      attachment_forward: user?.permission?.attachment_forward,
      attachment_send: user?.permission?.attachment_send,
      block_users: user?.permission?.block_users,
      camera_access: user?.permission?.camera_access,
      delete_message: user?.permission?.delete_message,
      edit_message: user?.permission?.edit_message,
      emergency_message: user?.permission?.emergency_message,
      file_access: user?.permission?.file_access,
      gallery_access: user?.permission?.gallery_access,
      invite_guests: user?.permission?.invite_guests,
      location_share: user?.permission?.location_share,
      manage_rooms: user?.permission?.manage_rooms,
      voice_message: user?.permission?.voice_message,
      search_users: user?.permission?.search_users,
      send_message: user?.permission?.send_message,
      video_call: user?.permission?.video_call,
      voice_call: user?.permission?.voice_call,
      // need timestamp in milliseconds
      creation_ts_ms: user["create_date"] * 1000,
    }),
    data: "users",
    total: json => json.data,
  },
  roles_permissions: {
    path: "/admin/users",
    map: user => ({
      ...user,
      id: user.id,
      name: `${user["first_name"]} ${user["last_name"]}`,
      userName: user.username,
      deactivated: !!user["mebis_deactivated"],
      add_guest_to_room: user?.permission?.add_guest_to_room,
      attachment_forward: user?.permission?.attachment_forward,
      attachment_send: user?.permission?.attachment_send,
      block_users: user?.permission?.block_users,
      camera_access: user?.permission?.camera_access,
      delete_message: user?.permission?.delete_message,
      edit_message: user?.permission?.edit_message,
      emergency_message: user?.permission?.emergency_message,
      file_access: user?.permission?.file_access,
      gallery_access: user?.permission?.gallery_access,
      invite_guests: user?.permission?.invite_guests,
      location_share: user?.permission?.location_share,
      manage_rooms: user?.permission?.manage_rooms,
      voice_message: user?.permission?.voice_message,
      search_users: user?.permission?.search_users,
      send_message: user?.permission?.send_message,
      video_call: user?.permission?.video_call,
      voice_call: user?.permission?.voice_call,
      // need timestamp in milliseconds
      creation_ts_ms: user["create_date"] * 1000,
    }),
    data: "users",
    total: json => json.data,
  },
};

// TODO refactor dataIntermediaryProvider to remove duplicateCode

const dataIntermediaryProvider = {
  getList: (resource, params) => {
    const { user_id, name, deactivated, search_term } = params.filter;
    const { page, perPage } = params.pagination;
    const { field, order } = params.sort;

    const fields = {
      avatar_url: "avatar",
      name: "username",
      uid: "uid",
      last_name: "last_name",
      role: "role_slug",
      displayname: "first_name",
      admin: "admin_staff",
      deactivated: "mebis_deactivated",
      create_date: "create_date",
      add_guest_to_room: "permission.add_guest_to_room",
      attachment_forward: "permission.attachment_forward",
      attachment_send: "permission.attachment_send",
      block_users: "permission.block_users",
      camera_access: "permission.camera_access",
      delete_message: "permission.delete_message",
      edit_message: "permission.edit_message",
      emergency_message: "permission.emergency_message",
      file_access: "permission.file_access",
      gallery_access: "permission.gallery_access",
      invite_guests: "permission.invite_guests",
      location_share: "permission.location_share",
      manage_rooms: "permission.manage_rooms",
      voice_message: "permission.voice_message",
      search_users: "permission.search_users",
      send_message: "permission.send_message",
      video_call: "permission.video_call",
      voice_call: "permission.voice_call",
    };

    const query = {
      from: page,
      limit: perPage,
      user_id: user_id,
      search_term: search_term,
      name: name,
      mebis_deactivated: deactivated,
      order_by: fields[field],
      dir: order,
    };

    const homeserver = baseURl;
    if (!homeserver || !(resource in resourceMap)) return Promise.reject();

    const res = resourceMap[resource];
    if (res.data === "devices") {
      const endpoint_url = homeserver + res.path + `${user_id}/devices`;
      return jsonClient(endpoint_url).then(({ json }) => {
        const result = {
          data: json.data,
          total: json.data.length,
        };
        result.data.forEach(r => {
          r.id = uuidv4();
        });
        return result;
      });
    }

    // Combine users and roles tabs
    // https://sdui.atlassian.net/browse/BYCS-3271
    // Adjust sort query params for users route
    // if (resource === "users") {
    //   // sort by role
    //   if (query.order_by === "role_slug") {
    //     query.order_by = `${query.order_by} ${query.dir},last_name ASC`;
    //     delete query.dir;
    //   }

    // sort deactivated users by role
    //   if (query.order_by === "mebis_deactivated") {
    //     query.order_by = `role_slug ASC,${query.order_by} ${query.dir}`;
    //     delete query.dir;
    //   }
    // }

    // Adjust sort query params for roles_permissions route
    if (resource === "users" || resource === "roles_permissions") {
      if (query.order_by === "mebis_deactivated") {
        query.order_by = `${query.order_by} ${query.dir}`;
      } else if (query.order_by.includes("permission")) {
        query.order_by = `mebis_deactivated ASC,role_slug ASC,${query.order_by} ${query.dir}`;
      } else {
        query.order_by = `mebis_deactivated ASC,${query.order_by} ${query.dir}`;
      }
      delete query.dir;
    }

    const endpoint_url = homeserver + res.path;
    const url = `${endpoint_url}?${stringify(query)}`;

    return jsonClient(url).then(({ json }) => ({
      data: json.data.items.map(res.map),
      total: json.data.total,
    }));
  },

  getOne: async (resource, params) => {
    const homeserver = baseURl;
    if (!homeserver || !(resource in resourceMap)) return Promise.reject();

    const list = await dataIntermediaryProvider.getNoTotalList("users");

    if (!list) {
      return;
    }

    const userEntry = getPickedUser(list?.data, params.id);

    const res = resourceMap[resource];
    const endpoint_url = homeserver + res.path;

    return jsonClient(`${endpoint_url}/${userEntry?.uid}`).then(({ json }) => ({
      data: res.map(json),
    }));
  },

  getNoTotalList: async (resource, params) => {
    const homeserver = baseURl;
    if (!homeserver || !(resource in resourceMap)) return Promise.reject();
    const query = {
      name: params?.name,
      role: params?.role,
    };
    const res = resourceMap[resource];

    const endpoint_url = homeserver + res.path;
    const url = params
      ? `${endpoint_url}?${stringify(query)}`
      : `${endpoint_url}`;
    return jsonClient(url).then(({ json }) => ({
      data: json.data,
    }));
  },

  updatePermissions: (permissionsList, userIdsList) => {
    permissionsList["video_call"] = false;
    permissionsList.attachment_send = false;
    try {
      return fetch(`${baseURl}/admin/users`, {
        headers: {
          Authorization: "Bearer " + getToken(),
          "Content-Type": "application/json",
        },
        method: "PATCH",
        body: JSON.stringify({
          users: userIdsList,
          permission: permissionsList,
        }),
      })
        .then(response => response.json())
        .then(() => {
          window.dispatchEvent(permissionEvent);
          toast.success(
            checkLanguage().resources.users.success_msg_permissions
          );
        });
    } catch (er) {
      console.error(er);
      toast.warning(checkLanguage().synapseadmin.users.err_msg);
    }
  },

  updateUser: async (data, userId, successMsg) => {
    try {
      const list = await dataIntermediaryProvider.getNoTotalList("users");

      if (!list) {
        console.error("Update User usersList not fetched");
        return;
      }

      const userEntry = getPickedUser(list.data, userId);
      return fetch(
        `${baseURl}/admin/users/${userEntry.uid}`,
        {
          headers: {
            Authorization: "Bearer " + getToken(),
            "Content-Type": "application/json",
          },
          method: "PATCH",
          body: JSON.stringify({
            mebis_deactivated: data?.deactivated,
            admin_staff: data?.admin,
            permission: {
              file_access: data?.permissions?.file_access,
              send_message: data?.permissions?.send_message,
              edit_message: data?.permissions?.edit_message,
              delete_message: data?.permissions?.delete_message,
              gallery_access: data?.permissions?.gallery_access,
              voice_call: data?.permissions?.voice_call,
              video_call: false,
              location_share: data?.permissions?.location_share,
              emergency_message: data?.permissions?.emergency_message,
              attachment_send: data?.permissions?.attachment_send,
              attachment_forward: data?.permissions?.attachment_forward,
              camera_access: data?.permissions?.camera_access,
              voice_message: data?.permissions?.voice_message,
              manage_rooms: data?.permissions?.manage_rooms,
              block_users: data?.permissions?.block_users,
              search_users: data?.permissions?.search_users,
              invite_guests: data?.permissions?.invite_guests,
              add_guest_to_room: false,
            },
          }),
        },
        successMsg
      )
        .then(response => response.json())
        .then(() => {
          window.dispatchEvent(permissionEvent);
          toast.success(checkLanguage().resources.users[successMsg]);
        });
    } catch (er) {
      toast.warning(checkLanguage().synapseadmin.users.err_msg);
      console.error(er);
    }
  },

  updateRestrictionActivationData: (
    data,
    showSuccessToast = true,
    successMsg
  ) => {
    const payload = {
      users: data.users,
    };

    if (data.mebisDeactivated !== undefined) {
      payload.mebis_deactivated = data.mebisDeactivated;
    }

    if (data.accessRestricted !== undefined) {
      payload.access_restricted = data.accessRestricted;
    }

    try {
      return fetch(`${baseURl}/admin/users`, {
        headers: {
          Authorization: "Bearer " + getToken(),
          "Content-Type": "application/json",
        },
        method: "PATCH",
        body: JSON.stringify(payload),
      })
        .then(response => response.json())
        .then(() => {
          showSuccessToast &&
            toast.success(checkLanguage().resources.users[successMsg]);
        });
    } catch (er) {
      console.error(er);
      toast.warning(checkLanguage().resources.users.err_msg);
    }
  },

  updateRestrictionActivationForLoggedUser: (
    data,
    showSuccessToast = true,
    successMsg
  ) => {
    const payload = {
      users: data.users,
    };

    if (data.mebisDeactivated !== undefined) {
      payload.mebis_deactivated = data.mebisDeactivated;
    }

    if (data.accessRestricted !== undefined) {
      payload.access_restricted = data.accessRestricted;
    }

    try {
      return fetch(`${baseURl}/admin/users/${localStorage.getItem("userId")}`, {
        headers: {
          Authorization: "Bearer " + getToken(),
          "Content-Type": "application/json",
        },
        method: "PATCH",
        body: JSON.stringify(payload),
      })
        .then(response => response.json())
        .then(() => {
          showSuccessToast &&
            toast.success(checkLanguage().resources.users[successMsg]);
        });
    } catch (er) {
      console.error(er);
      toast.warning(checkLanguage().resources.users.err_msg);
    }
  },

  getRestrictionActivationForLoggedUser: () => {
    try {
      return fetch(`${baseURl}/admin/users/${localStorage.getItem("userId")}`, {
        headers: {
          Authorization: "Bearer " + getToken(),
          "Content-Type": "application/json",
        },
        method: "GET",
      }).then(response => response.json());
    } catch (er) {
      console.error(er);
      toast.warning(checkLanguage().resources.users.err_msg);
    }
  },

  updateSingleSN: async (data, userId) => {
    try {
      const list = await dataIntermediaryProvider.getNoTotalList("users");

      if (!list) {
        console.error("Update User usersList not fetched");
        return;
      }

      const userEntry = getPickedUser(list.data, userId);
      return fetch(`${baseURl}/admin/users/${userEntry.uid}`, {
        headers: {
          Authorization: "Bearer " + getToken(),
          "Content-Type": "application/json",
        },
        method: "PATCH",
        body: JSON.stringify({
          silent_notifications: data,
        }),
      }).then(() => {
        toast.success(
          checkLanguage().synapseadmin.users.silent_notifications.success_msg
        );
      });
    } catch (er) {
      console.error(er);
      toast.warning(checkLanguage().synapseadmin.users.err_msg);
    }
  },
  updateSilentModification: (users, silentNotifications) => {
    if (!silentNotifications.allow_notifications_on_weekends) {
      silentNotifications.allow_notifications_on_weekends = false;
    }

    const payload = {
      users: users,
      silent_notifications: silentNotifications,
    };

    try {
      return fetch(`${baseURl}/admin/users`, {
        headers: {
          Authorization: "Bearer " + getToken(),
          "Content-Type": "application/json",
        },
        method: "PATCH",
        body: JSON.stringify(payload),
      })
        .then(response => response.json())
        .then(() => {
          toast.success(
            checkLanguage().synapseadmin.users.silent_notifications.success_msg
          );
        })
        .catch(er => er);
    } catch (er) {
      console.error(er);
      toast.warning(checkLanguage().synapseadmin.users.err_msg);
    }
  },

  updateSchoolLogo: payload => {
    try {
      return fetch(baseURl + "/admin/school/logo", {
        method: "POST",
        body: payload,
        headers: {
          Authorization: "Bearer " + getToken(),
        },
      }).then(() => {
        toast.success(
          checkLanguage().resources.school_settings.avatar.success_msg
        );
      });
    } catch (er) {
      console.error(er);
      toast.warning(checkLanguage().resources.school_settings.avatar.err_msg);
    }
  },

  getInitialSchoolLogo: () => {
    return jsonClient(baseURl + "/admin/school").then(({ json }) => ({
      data: json.data,
    }));
  },
};

export default dataIntermediaryProvider;
